import React, { useEffect, useRef } from 'react';
import { Grid, Typography, TextField, Button, FormControl, Select, MenuItem, styled, Tooltip, capitalize } from '@material-ui/core';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import TeamFilterHierarchyOutlined from './TeamFilterHierarchyOutlined';
import { tooltipClasses } from '@mui/material';
import { appService } from '../App/app.service';
import { connect } from 'react-redux';


const validationSchema = Yup.object().shape({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  email: Yup.string().email('Invalid email').required('Email is required'),
  roleType: Yup.string().required('Role type is required'),
  team: Yup.string().when('roleType', {
    is: (val) => !['admin','c-level'].includes(val),
    then: Yup.string().required('Team is required'),
    otherwise: Yup.string(),
  }),
});


const CustomWidthTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    minWidth: "none",
    padding: '1rem',
    fontSize: "1rem",
    color: "black",
    fontWeight:400,
    background: "white",
    border: "0.5px solid lightgrey",
  },
});

const ProvisionUserForm = ({
  title,
  initialValues,
  teamRoleInfo,
  disableTeam,
  onBack,
  onSubmit,
  backIcon,
  isEditable = true,
  handleChangeTeam,
  handleChangeRoleType,
  team,
  checkEmail,
  isNonCRMEmailExists,
  setView,
  loading,
  inviteError,
  setRoleType,
  clearInviteError
}) => {
  useEffect(()=>{
    return ()=>{
      // remove or clear parent states regarding the current selected user
      setRoleType('')
    }
  },[])
  
  const formikRef = useRef();

  console.log("RolesCheck", teamRoleInfo.roleTypes);
  console.log("teamRoleInfo_loading",team)
  
  useEffect(()=>{ 
    if(teamRoleInfo !== "" && teamRoleInfo.teamAndRoleType !== null && teamRoleInfo.teamAndRoleType !== undefined){
      console.log("teamRoleInfo_loading",teamRoleInfo,teamRoleInfo.teamAndRoleType.team,teamRoleInfo.teamAndRoleType.roleType)
      formikRef.current.setFieldValue('roleType', teamRoleInfo.teamAndRoleType.roleType);
      formikRef.current.setFieldValue('team', teamRoleInfo.teamAndRoleType.team);
    }
  },[teamRoleInfo])
  
  useEffect(() => {
    if (formikRef.current && team !== formikRef.current.values.team) {
      formikRef.current.setFieldValue('team', team);
    }
  }, [team]);

  const disableFields = !isEditable;

  const textFieldStyle = {
    background: '#fafafd',
    borderRadius: 8,
    fontSize: '0.95rem',
    width: '100%',
  };

  const labelStyle = {
    fontWeight: 500,
    marginBottom: 6,
    fontSize: '0.95rem',
    display: 'block',
  };

  const gridItemStyle = {
    padding: '0.75rem 1rem 0.75rem 0rem',
  };

  /**
   * EVALUATE PROVISION USER BUTTON STATE
   * 
   * This function implements the state machine logic to determine whether
   * the "Provision User" button should be enabled or disabled.
   * 
   * @param {Object} values - Current form values from Formik
   * @param {Object} errors - Current validation errors from Formik
   * @param {boolean} loading - Loading state indicator
   * 
   * Logic Flow:
   * -----------
   * Step 1: Check if loading - if true, button is disabled
   * Step 2: Validate required fields based on role type:
   *         - Admin: firstName, lastName, email, roleType
   *         - Rep/Manager: firstName, lastName, email, roleType, team
   * Step 3: Filter errors based on role type
   *         - For Admin: ignore 'team' validation errors
   *         - For Rep/Manager: consider all validation errors
   * Step 4: Check if any relevant errors exist - if yes, button is disabled
   * Step 5: If all conditions pass, button is enabled
   * 
   * Returns: boolean (true = enabled, false = disabled)
   */
  const evaluateProvisionButtonState = (values, errors, loading) => {
    // Step 1: If loading, disable button
    if (loading) {
      return false;
    }

    // Step 2: Check required fields based on role type
    let hasRequiredFields = Boolean(
      values.firstName !== "" && 
      values.lastName !== "" && 
      values.email  !== "" &&  !Object.keys(errors).includes("email") && 
      values.roleType !== ""
    );
    
    if (["admin",'c-level'].includes(values.roleType)) {
      // Admin role: team is not required
      return hasRequiredFields
    } else {
      // Rep/Manager roles: all fields including team are required
      return hasRequiredFields && Boolean(
        values.team !== ""
      );
    }
  };
  
  return (
    <div style={{ position: 'relative', minHeight: '100%' }}>
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        enableReinitialize={false}
      >
        {({ values, errors, touched, handleChange, setFieldValue, dirty }) => {
            /**
             * EVALUATE PROVISION BUTTON STATE ON RENDER
             * 
             * Calculate button state directly during render - no state variable needed.
             * This ensures the button state is always in sync with the form state
             * without causing re-render issues.
             */
            const isProvisionButtonEnabled = evaluateProvisionButtonState(values, errors, loading);
            
            // Debug logging with common key for filtering
            console.log('[PROVISION_FORM_DEBUG] === Form State Check ===');
            console.log('[PROVISION_FORM_DEBUG] Current Values:', values);
            console.log('[PROVISION_FORM_DEBUG] Current Errors:', errors);
            console.log('[PROVISION_FORM_DEBUG] Loading State:', loading);
            console.log('[PROVISION_FORM_DEBUG] Role Type:', values.roleType);
            console.log('[PROVISION_FORM_DEBUG] Team Value:', values.team);
            console.log('[PROVISION_FORM_DEBUG] Button Enabled:', isProvisionButtonEnabled);
            console.log('[PROVISION_FORM_DEBUG] ================================');

            return <Form>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                {onBack && (
                  <Button onClick={onBack} style={{ marginBottom: 8 }}>
                    {backIcon}
                  </Button>
                )}
                <Typography variant="h6" style={{ fontWeight: '600', marginTop: '1rem', marginBottom: '1rem' }}>
                  {title}
                </Typography>
              </Grid>

              {/* First Row */}
              <Grid container item xs={12} spacing={2}>
                <Grid item xs={12} sm={4} style={gridItemStyle}>
                  <Typography style={labelStyle}>First Name</Typography>
                  <TextField
                    name="firstName"
                    variant="outlined"
                    fullWidth
                    value={values.firstName}
                    onChange={e => {
                      e.target.value = e.target.value.trimStart();
                      handleChange(e);
                    }}
                    onBlur={e => {
                      e.target.value = e.target.value.trim();
                      handleChange(e);
                    }}
                    error={touched.firstName && Boolean(errors.firstName)}
                    helperText={touched.firstName && errors.firstName}
                    placeholder="First Name"
                    InputProps={{ style: textFieldStyle }}
                    InputLabelProps={{ style: { fontSize: '0.95rem' } }}
                    inputProps={{ style: { fontSize: '0.95rem' } }}
                    disabled={disableFields}
                  />
                </Grid>

                <Grid item xs={12} sm={4} style={gridItemStyle}>
                  <Typography style={labelStyle}>Last Name</Typography>
                  <TextField
                    name="lastName"
                    variant="outlined"
                    fullWidth
                    value={values.lastName}
                    onChange={e => {
                      e.target.value = e.target.value.trimStart();
                      handleChange(e);
                    }}
                    onBlur={e => {
                      e.target.value = e.target.value.trim();
                      handleChange(e);
                    }}
                    error={touched.lastName && Boolean(errors.lastName)}
                    helperText={touched.lastName && errors.lastName}
                    placeholder="Last Name"
                    InputProps={{ style: textFieldStyle }}
                    InputLabelProps={{ style: { fontSize: '0.95rem' } }}
                    inputProps={{ style: { fontSize: '0.95rem' } }}
                    disabled={disableFields}
                  />
                </Grid>

                <Grid item xs={12} sm={4} style={gridItemStyle}>
                  <Typography style={labelStyle}>Email</Typography>
                  <TextField
                    name="email"
                    variant="outlined"
                    fullWidth
                    value={values.email}
                    onChange={e => {
                      e.target.value = e.target.value.trimStart();
                      handleChange(e);
                      clearInviteError()
                    }}
                    onBlur={e => {
                      e.target.value = e.target.value.trim();
                      handleChange(e);
                    }}
                    error={touched.email && (Boolean(errors.email) || isNonCRMEmailExists)}
                    helperText={
                      touched.email && errors.email
                        ? errors.email
                        : isNonCRMEmailExists
                          ? <span style={{ color: '#d32f2f' }}>This email already belongs to a SkyGeni user.</span>
                          : inviteError
                            ? <span style={{ color: '#d32f2f' }}>{inviteError}</span>
                            : ''
                    }
                    placeholder="Email"
                    InputProps={{
                      style: {
                        ...textFieldStyle,
                        border: isNonCRMEmailExists ? '1.5px solid #d32f2f' : undefined,
                        fontSize: '0.95rem'
                      }
                    }}
                    inputProps={{ style: { fontSize: '0.95rem' } }}
                    disabled={disableFields}
                  />
                </Grid>
              </Grid>

              {/* Second Row */}
              <Grid container item xs={12} spacing={2}>
                <Grid item xs={12} sm={4} style={gridItemStyle}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Typography style={labelStyle}>Role Type</Typography>
                    <CustomWidthTooltip
                      title={
                        <>
                          <div>
                            <b>Admin:</b><br />
                            • Full admin controls (user management, editing quotas)<br />
                            • Access to all dashboards and company data
                          </div>
                          <div style={{ marginTop: 8 }}>
                            <b>Manager:</b><br />
                            • Access to assigned dashboards only<br />
                            • Can view team data and all sub-teams/reps
                          </div>
                          <div style={{ marginTop: 8 }}>
                            <b>Rep:</b><br />
                            • Access to assigned dashboards only<br />
                            • Can only view their own data
                          </div>
                        </>
                      }
                      placement="right"
                      arrow
                    >
                      <span
                        style={{
                          marginLeft: 6,
                          cursor: 'pointer',
                          display: 'flow',
                          alignItems: 'center',
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#4472c4"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <circle cx="12" cy="12" r="10" />
                          <line x1="12" y1="16" x2="12" y2="12" />
                          <line x1="12" y1="8" x2="12" y2="8" />
                        </svg>
                      </span>
                    </CustomWidthTooltip>
                  </div>

                  <FormControl fullWidth variant="outlined">
                    <Select
                      name="roleType"
                      value={values.roleType}
                      displayEmpty
                      renderValue={selected => {
                        if (!selected || selected === "") {
                          return <span style={{ color: '#aaa' }}>Please Select a Role Type</span>;
                        }
                        return capitalize(selected);
                      }}
                      onChange={ev => {
                        // Update Role Type and invoke state machine transition
                        handleChangeRoleType(ev);
                        handleChange(ev);
                        
                        // Reset Team filter based on new Role Type selection
                        handleChangeTeam('');
                        setFieldValue('team', '');
                      }}
                      style={{ borderRadius: 8, fontSize: '0.95rem', background: '#fafafd' }}
                    >
                      {teamRoleInfo?.roleTypes?.map(role => (
                        <MenuItem key={role} value={role} style={{ fontSize: '0.95rem' }}>
                          {capitalize(role)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={4} style={gridItemStyle}>
                  <Typography style={labelStyle}>Team</Typography>
                  {["admin","c-level"].includes(values.roleType) ? (
                    <TextField
                      variant="outlined"
                      fullWidth
                      value={"Not Applicable"}
                      disabled
                      InputProps={{ style: { ...textFieldStyle, fontSize: '0.95rem',background:'rgb(250, 250, 253)' } }}
                      inputProps={{ style: { fontSize: '0.95rem' } }}
                    />
                  ) : (
                    teamRoleInfo !== "" ? (
                      <TeamFilterHierarchyOutlined
                        label="Team:"
                        value={team}
                        onTeamChange={val => {
                          handleChangeTeam(val);
                          setFieldValue('team', val);
                        }}
                        name="team"
                        data={teamRoleInfo.teams.parentChildrenTeamFilter.filters}
                        disabled={disableTeam || values.roleType === ""}
                        labelColorCondition={false}
                        renderValue={selected => selected === "" ? "Please Select a Team" : selected}
                        getOptionLabel={option => option.label}
                        getOptionValue={option => option.parentValue + option.label}
                        style={{ width: '100%', background: '#fafafd', borderRadius: 8, fontSize: '0.95rem' }}
                      />
                    ) : (
                      <Typography variant="body2" color="textSecondary" style={{ fontSize: '0.85rem' }}>
                        Loading team data...
                      </Typography>
                    )
                  )}
                </Grid>

                <Grid item xs={12} sm={4} style={gridItemStyle}>
                  <Typography style={labelStyle}>Status</Typography>
                  <TextField
                    variant="outlined"
                    fullWidth
                    value={'Active'}
                    placeholder="Status"
                    InputProps={{ style: { ...textFieldStyle, fontSize: '0.95rem' } }}
                    inputProps={{ style: { fontSize: '0.95rem' } }}
                    disabled
                  />
                </Grid>
              </Grid>

              {/* Buttons */}
              <Grid item xs={12} container style={{paddingLeft:0}}>
                <Grid item xs={12} sm={2} style={gridItemStyle} container>
                  {console.log(Object.keys(errors),errors,'values_checking')}
                  {/* 
                    PROVISION USER BUTTON
                    
                    This button is controlled by the isProvisionButtonEnabled variable,
                    which is computed directly during render by the evaluateProvisionButtonState function.
                    
                    The button will be DISABLED when:
                    - Form is submitting (loading = true)
                    - Validation errors exist (filtered by role type)
                    - Required fields are not filled (based on role type)
                    
                    The button will be ENABLED when:
                    - Form is not submitting (loading = false)
                    - No validation errors (or only 'team' error for Admin)
                    - All required fields are filled (firstName, lastName, email, roleType, and team if not Admin)
                  */}
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    fullWidth
                    disabled={!isProvisionButtonEnabled}
                  >
                    Provision User
                  </Button>
                </Grid>

                <Grid item xs={12} sm={2} style={gridItemStyle} container>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => setView("activeUserManagement")}
                    fullWidth
                  >
                    Cancel
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Form>
          }
        }
      </Formik>

      {loading && (
        <div
          style={{
            position: 'absolute',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <img
            src="/test_loading.gif"
            alt="Loading..."
            style={{ width: '50px', height: '50px' }}
          />
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state) => {
  const { user, isNonCRMEmailExists,inviteError } = state.app;
  return {
    isNonCRMEmailExists,
    user,
    Value_Label: user.Value_Label,
    inviteError
  };
};

const mapDispatchToProps = (dispatch) => ({
  clearInviteError:()=>{
    dispatch({type:"clear_invite_error"})
  },
  checkEmail: (email) => {
    dispatch({ type: "verify_user_request" });
    appService.checkEmail(email).then(
      (json) => {
        dispatch({ type: "verify_user_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "verify_user_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "verify_user_failure", error });
      }
    );
  },
});

const ProvisionUserFormSection = connect(
  mapStateToProps,
  mapDispatchToProps
)(ProvisionUserForm);

export default ProvisionUserFormSection;